<div class="top-navigation-bar">
  @if (menuConfig$ | async; as menuConfig) {
  
  <!-- Hamburger menu button (mobile only) -->
  <button class="hamburger-button" (click)="toggleMobileMenu()" aria-label="Toggle menu">
    <app-dynamic-icon [name]="mobileMenuOpen ? 'close-outline' : 'menu-outline'"></app-dynamic-icon>
  </button>

  <!-- Current route label (mobile only) -->
  <div class="current-route-label" (click)="toggleMobileMenu()" >
    <span>{{ currentRouteLabel }}</span>
  </div>

  <!-- Mobile menu overlay -->
  <div class="mobile-menu-overlay" [class.open]="mobileMenuOpen" (click)="closeMobileMenu()"></div>

  <!-- Main navigation sections -->
  <div class="nav-sections-container" [class.mobile-open]="mobileMenuOpen">
    @for (section of menuConfig.sections; track section.id) {
    <div class="nav-section" 
         [class.expanded]="isExpandedSection(section.id)"
         #sectionElement>
      
      <!-- Section header/trigger -->
      <button class="section-header" 
              [class.active]="isExpandedSection(section.id)"
              [class.route-active]="isSectionActive(section.id)"
              (mouseenter)="!isMobile && expandSection(section.id, sectionElement)"
              (mouseleave)="!isMobile && startCollapseTimer(section.id)"
              (click)="toggleSection(section.id, $event, sectionElement)">
        <span class="section-label">{{ getLabel(section.labelKey, section.labels) }}</span>
        <app-dynamic-icon name="chevron-down-outline" class="section-icon" [class.rotated]="isExpandedSection(section.id)"></app-dynamic-icon>
      </button>

      <!-- Section items dropdown -->
      @if (isExpandedSection(section.id)) {
      <div class="section-items-dropdown"
           [style.left.px]="dropdownLeft"
           (mouseenter)="!isMobile && cancelCollapseTimer()"
           (mouseleave)="!isMobile && collapseSection(section.id)">
        @for (item of section.items; track item.id) {
        
        <!-- Simple menu item without children -->
        @if (!hasChildren(item)) {
        <a [routerLink]="item.route" 
           routerLinkActive="active" 
           class="section-item" 
           [class.disabled]="item.disabled"
           (click)="onItemClick(section.id); closeMobileMenu()">
          @if (item.icon) {
            <app-dynamic-icon [name]="item.icon"></app-dynamic-icon>
          }
          <span class="item-label">{{ getLabel(item.labelKey, item.labels) }}</span>
          @if (item.badge) {
          <ion-badge [color]="item.badge.color || 'primary'" class="item-badge">
            {{ item.badge.text }}
          </ion-badge>
          }
        </a>
        }

        <!-- Menu item with children (nested submenu) -->
        @if (hasChildren(item)) {
        <div class="section-item-with-submenu" [class.expanded]="isExpanded(item.id)">
          <button class="section-item" 
                  [class.active]="isExpanded(item.id)"
                  (click)="toggleSubmenu(item.id, $event)">
            @if (item.icon) {
              <app-dynamic-icon [name]="item.icon"></app-dynamic-icon>
            }
            <span class="item-label">{{ getLabel(item.labelKey, item.labels) }}</span>
            <app-dynamic-icon name="chevron-forward-outline" class="submenu-icon" [class.rotated]="isExpanded(item.id)"></app-dynamic-icon>
            @if (item.badge) {
            <ion-badge [color]="item.badge.color || 'primary'" class="item-badge">
              {{ item.badge.text }}
            </ion-badge>
            }
          </button>

          <!-- Nested submenu -->
          @if (isExpanded(item.id)) {
          <div class="nested-submenu">
            @for (child of item.children; track child.id) {
            <a [routerLink]="child.route" 
               routerLinkActive="active" 
               class="submenu-item" 
               [class.disabled]="child.disabled"
               (click)="onSubmenuClick(section.id, item.id); closeMobileMenu()">
              @if (child.icon) {
                <app-dynamic-icon [name]="child.icon"></app-dynamic-icon>
              }
              <span>{{ getLabel(child.labelKey, child.labels) }}</span>
              @if (child.badge) {
              <ion-badge [color]="child.badge.color || 'primary'">
                {{ child.badge.text }}
              </ion-badge>
              }
            </a>
            }
          </div>
          }
        </div>
        }

        @if (item.divider) {
        <div class="item-divider"></div>
        }
        }
      </div>
      }
    </div>
    }
  </div>

  <div class="due-reminder-count" >
    @if (dueRemindersCount > 0) {
      <app-dynamic-icon name="fa-solid fa-bell"></app-dynamic-icon>
      <span class="badge">{{ dueRemindersCount }}</span>
    } @else if (dueRemindersCount === 0) {
      <app-dynamic-icon name="fa-regular fa-bell-slash"></app-dynamic-icon>
    }
  </div>

  <!-- Context Selection (Combined Client/Subsidiary) -->
  @if (showClientSubsidiarySelection && (authService.isAuthenticated$ | async)) {
  <div class="context-selection">
    @if (contextOptions$ | async; as options) {
      @if (options.length > 0) {
      <div class="context-select-wrapper">
        <ion-select
          [value]="getContextValue()"
          (ionChange)="onContextChange($event)"
          interface="popover"
          [placeholder]="getContextPlaceholder() | translate"
          class="context-select">
          @for (option of options; track option.value) {
          <ion-select-option [value]="option.value">
            {{ option.label }}
          </ion-select-option>
          }
        </ion-select>
      </div>
      }
    }
  </div>
  }

  <!-- Context menu button (mobile only) -->
  @if (showClientSubsidiarySelection && (authService.isAuthenticated$ | async)) {
    @if (contextOptions$ | async; as options) {
      @if (options.length > 0) {
      <button class="context-menu-button" (click)="toggleContextMenu()" aria-label="Toggle context menu">
        <app-dynamic-icon name="fa-regular fa-building"></app-dynamic-icon>
      </button>
      }
    }
  }

  <!-- Context menu overlay -->
  <div class="context-menu-overlay" [class.open]="contextMenuOpen" (click)="closeContextMenu()"></div>

  <!-- Context menu drawer (mobile only) -->
  @if (showClientSubsidiarySelection && (authService.isAuthenticated$ | async)) {
  <div class="context-menu-drawer" [class.open]="contextMenuOpen">
    <div class="context-drawer-header">
      <h3>{{ 'CONTEXT.SELECT_CONTEXT' | translate }}</h3>
      <button class="close-drawer-button" (click)="closeContextMenu()" aria-label="Close">
        <app-dynamic-icon name="close-outline"></app-dynamic-icon>
      </button>
    </div>
    <div class="context-drawer-content">
      @if (contextOptions$ | async; as options) {
        @if (options.length > 0) {
        <div class="context-options-list">
          @for (option of options; track option.value) {
          <button 
            class="context-option-item" 
            [class.selected]="getContextValue() === option.value"
            (click)="onContextChange({detail: {value: option.value}}); closeContextMenu()">
            <app-dynamic-icon name="fa-regular fa-building" class="option-icon"></app-dynamic-icon>
            <span class="option-label">{{ option.label }}</span>
            @if (getContextValue() === option.value) {
            <app-dynamic-icon name="checkmark-outline" class="check-icon"></app-dynamic-icon>
            }
          </button>
          }
        </div>
        }
      }
    </div>
  </div>
  }

  <!-- Right-side actions -->
  @if (menuConfig.collapseOnMobile || !menuConfig.defaultCollapsed === false || (authService.isAuthenticated$ | async)) {
  <div class="nav-actions">
    <!-- @if (menuConfig.collapseOnMobile || !menuConfig.defaultCollapsed === false) {
    <button class="nav-item collapse-toggle" (click)="toggleCollapse()" aria-label="Toggle menu">
      <app-dynamic-icon [name]="(isCollapsed$ | async) ? 'chevron-down-outline' : 'chevron-up-outline'"></app-dynamic-icon>
    </button>
    } -->
    @if (authService.isAuthenticated$ | async) {
    <button class="nav-item logout-button" (click)="onLogout()" aria-label="Logout">
      <app-dynamic-icon name="log-out-outline" size="large"></app-dynamic-icon>
    </button>
    }
  </div>
  }
  }
</div>